import { Injectable, inject } from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import { HubConnection } from '@microsoft/signalr';

@Injectable()
export class ChatSignalrService extends AppComponentBase {
    chatHub!: HubConnection;
    isChatConnected = false;

    configureConnection(connection: any): void {
        // Set the common hub
        this.chatHub = connection;

        // Reconnect loop
        let reconnectTime = 5000;
        let tries = 1;
        let maxTries = 8;
        function start() {
            return new Promise(function (resolve: any, reject: any) {
                if (tries > maxTries) {
                    reject();
                } else {
                    connection
                        .start()
                        .then(resolve)
                        .then(() => {
                            reconnectTime = 5000;
                            tries = 1;
                        })
                        .catch(() => {
                            setTimeout(() => {
                                start().then(resolve);
                            }, reconnectTime);
                            reconnectTime *= 2;
                            tries += 1;
                        });
                }
            });
        }

        // Reconnect if hub disconnects
        connection.onclose((e: any) => {
            this.isChatConnected = false;

            if (e) {
                abp.log.debug('Chat connection closed with error: ' + e);
            } else {
                abp.log.debug('Chat disconnected');
            }

            start().then(() => {
                this.isChatConnected = true;
            });
        });

        // Register to get notifications
        this.registerChatEvents(connection);
    }

    registerChatEvents(connection: any): void {
        connection.on('getChatMessage', (message: any) => {
            abp.event.trigger('app.chat.messageReceived', message);
        });

        connection.on('getAllFriends', (friends: any) => {
            abp.event.trigger('abp.chat.friendListChanged', friends);
        });

        connection.on('getFriendshipRequest', (friendData: any, isOwnRequest: any) => {
            abp.event.trigger('app.chat.friendshipRequestReceived', friendData, isOwnRequest);
        });

        connection.on('getUserDeleted', (user: any) => {
            abp.event.trigger('app.chat.userDeleted', user);
        });

        connection.on('getUserConnectNotification', (friend: any, isConnected: any) => {
            abp.event.trigger('app.chat.userConnectionStateChanged', {
                friend: friend,
                isConnected: isConnected,
            });
        });

        connection.on('getUserStateChange', (friend: any, state: any) => {
            abp.event.trigger('app.chat.userStateChanged', {
                friend: friend,
                state: state,
            });
        });

        connection.on('getallUnreadMessagesOfUserRead', (friend: any) => {
            abp.event.trigger('app.chat.allUnreadMessagesOfUserRead', {
                friend: friend,
            });
        });

        connection.on('getReadStateChange', (friend: any) => {
            abp.event.trigger('app.chat.readStateChange', {
                friend: friend,
            });
        });
    }

    sendMessage(messageData: any, callback: any): void {
        if (!this.isChatConnected) {
            if (callback) {
                callback();
            }

            abp.notify.warn(this.l('ChatIsNotConnectedWarning'));
            return;
        }

        this.chatHub
            .invoke('sendMessage', messageData)
            .then((result: any) => {
                if (result) {
                    abp.notify.warn(result);
                }

                if (callback) {
                    callback();
                }
            })
            .catch((error: any) => {
                abp.log.error(error);

                if (callback) {
                    callback();
                }
            });
    }

    init(): void {
        abp.signalr.connect();
        abp.signalr
            .startConnection(abp.appPath + 'signalr-chat', (connection: any) => {
                this.configureConnection(connection);
            })
            .then(() => {
                abp.event.trigger('app.chat.connected');
                this.isChatConnected = true;
            });
    }
}
